import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import {
  Notification,
  NotificationNameActiveMap,
} from '../../Interfaces/Notifications/Notification';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { NotificationUser } from '../../Interfaces/Notifications/NotificationUser';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import { ApiEnums } from '../../Interfaces/ApiEnums';

export enum NotificationActionTypes {
  REQUEST_NOTIFICATION_TYPES = 'REQUEST_NOTIFICATION_TYPES',
  RECEIVED_NOTIFICATION_TYPES = 'RECEIVED_NOTIFICATION_TYPES',
  REGISTER_NOTIFICATION = 'REGISTER_NOTIFICATION',
  SAVE_NOTIFICATIONS_FILTER = 'SAVE_NOTIFICATION_FILTER',
  RESET_NOTIFICATIONS_FILTER = 'RESET_NOTIFICATIONS_FILTER',
  REQUEST_NOTIFICATION_NAMES = 'REQUEST_NOTIFICATION_NAMES',
  RECEIVED_NOTIFICATION_NAMES = 'RECEIVED_NOTIFICATION_NAMES',
  RECEIVED_NOTIFICATIONS_RULE_DATA = 'RECEIVED_NOTIFICATIONS_RULE_DATA',
  UPDATE_NOTIFICATIONS_RULE_DATA = 'UPDATE_NOTIFICATIONS_RULE_DATE',
  SEND_UNREGISTER_NOTIFICATION = 'SEND_UNREGISTER_NOTIFICATION',
  REQUEST_REGISTERED_USERS = 'REQUEST_REGISTERED_USERS',
  RECEIVED_REGISTERED_USERS = 'RECEIVED_REGISTERED_USERS',
  RECEIVED_USER_DATA = 'RECEIVED_USER_DATA',
  REGISTER_USER = 'REGISTER_USER',
  REGISTRATION_SUCCESS_MESSAGE = 'REGISTRATION_SUCCESS_MESSAGE',
  UPDATE_USER = 'UPDATE_USER',
  UPDATE_SUCCESS_MESSAGE = 'UPDATE_SUCCESS_MESSAGE',
  SEND_UNREGISTER_USER = 'SEND_UNREGISTER_USER',
  RECEIVED_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE',
  RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE = 'RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE',
  SCHEDULER_SERVICE_PING = 'SCHEDULER_SERVICE_PING',
  CLEAR_UNREGISTRATION_SUCCESS_MESSAGE = 'CLEAR_UNREGISTRATION_SUCCESS_MESSAGE',
  REQUEST_ACTIVE_NOTIFICATIONS = 'REQUEST_ACTIVE_NOTIFICATIONS',
  RECEIVED_ACTIVE_NOTIFICATIONS = 'RECEIVED_ACTIVE_NOTIFICATIONS',
  UPDATE_NOTIFICATION_ACTIVE = 'UPDATE_NOTIFICATION_ACTIVE',
  SAVE_NOTIFICATIONS_REPORT_FILTER = 'SAVE_NOTIFICATIONS_REPORT_FILTER',
  RESET_NOTIFICATIONS_REPORT_FILTER = 'RESET_NOTIFICATIONS_REPORT_FILTER',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface NotificationsState {
  isFetchingNotificationTypes: boolean;
  notificationTypes: string[];
  notificationsFilter: CompositeFilterDescriptor;
  isFetchingNotificationNames: boolean;
  notificationRuleNames: string[];
  notificationRuleData: Notification;
  isFetchingRegisteredUsers: boolean;
  registeredUsers: NotificationUser[];
  userData: NotificationUser;
  registrationSuccessMessage: string;
  unregistrationSuccessMessage: string;
  updateSuccessMessage: string;
  activeNotifications: NotificationNameActiveMap[];
  reportFilter: ReportFilter;
  fetchError: FetchError;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
export interface RequestNotificationTypesAction {
  type: NotificationActionTypes.REQUEST_NOTIFICATION_TYPES;
}

export interface ReceivedNotificationTypesAction {
  type: NotificationActionTypes.RECEIVED_NOTIFICATION_TYPES;
  values: string[];
}

export interface RegisterNotificationAction {
  type: NotificationActionTypes.REGISTER_NOTIFICATION;
  notificationData: Notification;
}

export interface SaveNotificationsReportFilterAction {
  type: NotificationActionTypes.SAVE_NOTIFICATIONS_REPORT_FILTER;
  reportFilter: ReportFilter;
}

export interface ResetNotificationsReportFilterAction {
  type: NotificationActionTypes.RESET_NOTIFICATIONS_REPORT_FILTER;
}

export interface SaveNotificationsFilterAction {
  type: NotificationActionTypes.SAVE_NOTIFICATIONS_FILTER;
  filters: CompositeFilterDescriptor;
}
export interface ResetNotificaitonsFilterAction {
  type: NotificationActionTypes.RESET_NOTIFICATIONS_FILTER;
}

export interface RequestNotificationNamesAction {
  type: NotificationActionTypes.REQUEST_NOTIFICATION_NAMES;
}

export interface ReceivedNotificationNamesAction {
  type: NotificationActionTypes.RECEIVED_NOTIFICATION_NAMES;
  values: string[];
}

export interface ReceivedNotificationsRuleDataAction {
  type: NotificationActionTypes.RECEIVED_NOTIFICATIONS_RULE_DATA;
  notificationRuleData: Notification;
}

export interface UpdateNotificationsRuleDataAction {
  type: NotificationActionTypes.UPDATE_NOTIFICATIONS_RULE_DATA;
}

export interface SendUnregisterNotificationAction {
  type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION;
  message: string;
}

export interface RequestRegisteredUsersAction {
  type: NotificationActionTypes.REQUEST_REGISTERED_USERS;
}

export interface ReceivedRegisteredUsersAction {
  type: NotificationActionTypes.RECEIVED_REGISTERED_USERS;
  values: NotificationUser[];
}

export interface ReceivedUserDataAction {
  type: NotificationActionTypes.RECEIVED_USER_DATA;
  userData: NotificationUser;
}

export interface SendUnregisterUserAction {
  type: NotificationActionTypes.SEND_UNREGISTER_USER;
  message: string;
}

export interface RegistrationSuccessMessageAction {
  type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE;
  message: string;
}

export interface UpdateSuccessMessageAction {
  type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE;
  message: string;
}

export interface ReceivedNotificationHttpErrorCodeAction {
  type: NotificationActionTypes.RECEIVED_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

export interface ResetNotificationHttpErrorCodeAction {
  type: NotificationActionTypes.RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE;
}

export interface SchedulerServicePing {
  type: NotificationActionTypes.SCHEDULER_SERVICE_PING;
}

export interface ClearUnregistrationSuccessMessageAction {
  type: NotificationActionTypes.CLEAR_UNREGISTRATION_SUCCESS_MESSAGE;
  message: string;
}

export interface RequestActiveNotificationsAction {
  type: NotificationActionTypes.REQUEST_ACTIVE_NOTIFICATIONS;
}

export interface ReceivedActiveNotificationsAction {
  type: NotificationActionTypes.RECEIVED_ACTIVE_NOTIFICATIONS;
  values: NotificationNameActiveMap[];
}

export interface UpdateNotificationActiveAction {
  type: NotificationActionTypes.UPDATE_NOTIFICATION_ACTIVE;
  index: number;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | RequestNotificationTypesAction
  | ReceivedNotificationTypesAction
  | RegisterNotificationAction
  | SaveNotificationsReportFilterAction
  | ResetNotificationsReportFilterAction
  | SaveNotificationsFilterAction
  | ResetNotificaitonsFilterAction
  | RequestNotificationNamesAction
  | ReceivedNotificationNamesAction
  | ReceivedNotificationsRuleDataAction
  | UpdateNotificationsRuleDataAction
  | SendUnregisterNotificationAction
  | RequestRegisteredUsersAction
  | ReceivedRegisteredUsersAction
  | ReceivedUserDataAction
  | SendUnregisterUserAction
  | RegistrationSuccessMessageAction
  | UpdateSuccessMessageAction
  | ReceivedNotificationHttpErrorCodeAction
  | ResetNotificationHttpErrorCodeAction
  | SchedulerServicePing
  | ClearUnregistrationSuccessMessageAction
  | RequestActiveNotificationsAction
  | ReceivedActiveNotificationsAction
  | UpdateNotificationActiveAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;

  console.error(`Notifications call failed, status: ${response.status}`);
  dispatch({
    type: NotificationActionTypes.RECEIVED_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE,
    statusCode: response.status,
  });
  return false;
};

export const actionCreators = {
  fetchNotificationTypes:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.REQUEST_NOTIFICATION_TYPES}`
      );
      dispatch({
        type: NotificationActionTypes.REQUEST_NOTIFICATION_TYPES,
      });

      const response = await fetch(
        `notifications/types`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_NOTIFICATION_TYPES}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_NOTIFICATION_TYPES,
        values: result,
      });
    },

  registerNotification:
    (notificationData: Notification): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `notifications/register`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          notificationData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      console.log(
        `Dispatching ${NotificationActionTypes.REGISTER_NOTIFICATION}`
      );

      dispatch({
        type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  updateNotification:
    (notificationData: Notification): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `notifications/edit`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          notificationData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
          message: 'success',
        });
        return;
      }

      console.log(
        `Dispatching ${NotificationActionTypes.UPDATE_NOTIFICATIONS_RULE_DATA}`
      );
      dispatch({
        type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  sendUnregisterNotification:
    (notification: Notification): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `notifications/unregister`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          notification
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION,
          message: 'failed',
        });
        return;
      }

      console.log(
        `Dispatching ${NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION}`
      );

      dispatch({
        type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION,
        message: 'success',
      });
    },

  saveNotificationsReportFilter:
    (reportFilter: ReportFilter): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.SAVE_NOTIFICATIONS_REPORT_FILTER}`
      );

      dispatch({
        type: NotificationActionTypes.SAVE_NOTIFICATIONS_REPORT_FILTER,
        reportFilter: reportFilter,
      });
    },

  resetNotificationsReportFilter:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      console.log(`Reseting Notifications Report Filter`);
      dispatch({
        type: NotificationActionTypes.RESET_NOTIFICATIONS_REPORT_FILTER,
      });
    },

  saveNotificationsFilter:
    (filters: CompositeFilterDescriptor): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Saving user-selected Notification Conditions Filter`);
      dispatch({
        type: NotificationActionTypes.SAVE_NOTIFICATIONS_FILTER,
        filters: filters,
      });
    },

  resetNotificationsFilter:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      console.log(`Reseting Notification Conditions Filter`);
      dispatch({
        type: NotificationActionTypes.RESET_NOTIFICATIONS_FILTER,
      });
    },

  fetchNotificationNames:
    (notificationType: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.REQUEST_NOTIFICATION_NAMES}`
      );
      dispatch({
        type: NotificationActionTypes.REQUEST_NOTIFICATION_NAMES,
      });

      const response = await fetch(
        `notifications/names/${notificationType}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_NOTIFICATION_NAMES} ${notificationType}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_NOTIFICATION_NAMES,
        values: result,
      });
    },

  fetchNotificationsRuleData:
    (
      notificationType: string,
      notificationName: string
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `notifications/${notificationType}/`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          notificationName
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as Notification;

      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_NOTIFICATIONS_RULE_DATA}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_NOTIFICATIONS_RULE_DATA,
        notificationRuleData: result,
      });
    },

  fetchRegisteredUsers:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.REQUEST_REGISTERED_USERS}`
      );
      dispatch({
        type: NotificationActionTypes.REQUEST_REGISTERED_USERS,
      });

      const response = await fetch(
        `users`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NotificationUser[];

      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_REGISTERED_USERS}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_REGISTERED_USERS,
        values: result,
      });
    },

  fetchUserData:
    (userName: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `users/userdata/${userName}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NotificationUser;

      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_USER_DATA} for ${userName}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_USER_DATA,
        userData: result,
      });
    },

  registerUser:
    (userData: NotificationUser): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `users/register`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          userData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      console.log(`Dispatching ${NotificationActionTypes.REGISTER_USER}`);

      dispatch({
        type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  sendUnregisterUser:
    (user: NotificationUser): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `users/unregister`,
        postRequestOptions(appState.userSessionReducer.sessionInfo?.Token, user)
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION,
          message: 'failed',
        });
        return;
      }

      console.log(
        `Dispatching ${NotificationActionTypes.SEND_UNREGISTER_USER}`
      );

      dispatch({
        type: NotificationActionTypes.SEND_UNREGISTER_USER,
        message: 'success',
      });
    },

  updateUser:
    (userData: NotificationUser): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `users/edit`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          userData
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      console.log(`Dispatching ${NotificationActionTypes.UPDATE_USER}`);

      dispatch({
        type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  resetFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE}`
      );

      dispatch({
        type: NotificationActionTypes.RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE,
      });
    },

  pingSchedulerService:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `Notifications/Ping`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });
      if (!isResponseOk(response, dispatch)) return;

      console.log(
        `Dispatching ${NotificationActionTypes.SCHEDULER_SERVICE_PING}`
      );

      dispatch({
        type: NotificationActionTypes.SCHEDULER_SERVICE_PING,
      });
    },

  clearRegistrationSuccessMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE,
        message: '',
      });
    },

  clearUpdateSuccessMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.UPDATE_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: NotificationActionTypes.UPDATE_SUCCESS_MESSAGE,
        message: '',
      });
    },

  clearUnregistrationSuccessMessage:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.CLEAR_UNREGISTRATION_SUCCESS_MESSAGE}`
      );
      dispatch({
        type: NotificationActionTypes.CLEAR_UNREGISTRATION_SUCCESS_MESSAGE,
        message: '',
      });
    },

  fetchActiveNotifications:
    (notificationType: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${NotificationActionTypes.REQUEST_ACTIVE_NOTIFICATIONS}`
      );
      dispatch({
        type: NotificationActionTypes.REQUEST_ACTIVE_NOTIFICATIONS,
      });

      const response = await fetch(
        `notifications/getactivenotifications/${notificationType}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });
      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NotificationNameActiveMap[];
      console.log(
        `Dispatching ${NotificationActionTypes.RECEIVED_ACTIVE_NOTIFICATIONS} ${notificationType}`
      );

      dispatch({
        type: NotificationActionTypes.RECEIVED_ACTIVE_NOTIFICATIONS,
        values: result,
      });
    },

  updateNotificationActive:
    (notificationType: string, index: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const entry: NotificationNameActiveMap =
        appState.notificationsReducer.activeNotifications[index];

      const response = await fetch(
        `notifications/updatenotificationactive/${
          entry.NotificationId
        }/${!entry.NotificationActive}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          entry.NotificationName
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      console.log(
        `Dispatching ${NotificationActionTypes.UPDATE_NOTIFICATION_ACTIVE}`
      );

      dispatch({
        type: NotificationActionTypes.UPDATE_NOTIFICATION_ACTIVE,
        index: index,
      });
    },
};

export const initialNotificationsFilter: CompositeFilterDescriptor = {
  logic: 'and',
  filters: [],
};

export const initialNotificationRuleData: Notification = {
  NotificationId: 0,
  NotificationName: '',
  NotificationType: '',
  NotificationRules: initialNotificationsFilter,
  NotificationActive: false,
  NotificationEnabled: false,
  NotificationFrequency: 0,
  UserIds: [],
  ScheduledTime: '',
};

export const initialUserData: NotificationUser = {
  UserId: 0,
  UserName: '',
  EmailAddress: '',
  PhoneNumber: '',
  Active: false,
};

export const initialReportsFilter: ReportFilter = {
  Division: '',
  SiteNumbers: [],
  LaneNumbers: [],
  DateType: '',
  StartDateTime: new Date(),
  EndDateTime: new Date(),
  DateRange: ApiEnums.DateRangeOptions.Today,
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: NotificationsState = {
  isFetchingNotificationTypes: false,
  notificationTypes: [],
  notificationsFilter: initialNotificationsFilter,
  isFetchingNotificationNames: false,
  isFetchingRegisteredUsers: false,
  notificationRuleNames: [],
  notificationRuleData: initialNotificationRuleData,
  registeredUsers: [],
  userData: initialUserData,
  registrationSuccessMessage: '',
  unregistrationSuccessMessage: '',
  updateSuccessMessage: '',
  fetchError: { StatusCode: 0, Message: '' },
  activeNotifications: [],
  reportFilter: initialReportsFilter,
};

export const reducer: Reducer<NotificationsState> = (
  state: NotificationsState | undefined,
  incomingAction: Action
): NotificationsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case NotificationActionTypes.REQUEST_NOTIFICATION_TYPES:
      return {
        ...state,
        isFetchingNotificationTypes: true,
      };
    case NotificationActionTypes.RECEIVED_NOTIFICATION_TYPES:
      return {
        ...state,
        isFetchingNotificationTypes: false,
        notificationTypes: action.values,
      };
    case NotificationActionTypes.REGISTER_NOTIFICATION:
      return {
        ...state,
      };

    case NotificationActionTypes.SAVE_NOTIFICATIONS_REPORT_FILTER:
      return {
        ...state,
        reportFilter: action.reportFilter,
      };
    case NotificationActionTypes.RESET_NOTIFICATIONS_REPORT_FILTER:
      return {
        ...state,
        reportFilter: initialReportsFilter,
      };
    case NotificationActionTypes.SAVE_NOTIFICATIONS_FILTER:
      return {
        ...state,
        notificationsFilter: action.filters,
      };
    case NotificationActionTypes.RESET_NOTIFICATIONS_FILTER:
      return {
        ...state,
        notificationsFilter: initialNotificationsFilter,
      };
    case NotificationActionTypes.REQUEST_NOTIFICATION_NAMES:
      return {
        ...state,
        isFetchingNotificationNames: true,
      };
    case NotificationActionTypes.RECEIVED_NOTIFICATION_NAMES:
      return {
        ...state,
        isFetchingNotificationNames: false,
        notificationRuleNames: action.values,
      };
    case NotificationActionTypes.RECEIVED_NOTIFICATIONS_RULE_DATA:
      return {
        ...state,
        notificationRuleData: action.notificationRuleData,
      };
    case NotificationActionTypes.SEND_UNREGISTER_NOTIFICATION:
      return {
        ...state,
        notificationRuleData: initialNotificationRuleData,
        unregistrationSuccessMessage: action.message,
      };
    case NotificationActionTypes.REGISTRATION_SUCCESS_MESSAGE:
      return {
        ...state,
        registrationSuccessMessage: action.message,
      };
    case NotificationActionTypes.REQUEST_REGISTERED_USERS:
      return {
        ...state,
        isFetchingRegisteredUsers: true,
      };
    case NotificationActionTypes.RECEIVED_REGISTERED_USERS:
      return {
        ...state,
        isFetchingRegisteredUsers: false,
        registeredUsers: action.values,
      };
    case NotificationActionTypes.RECEIVED_USER_DATA:
      return {
        ...state,
        userData: action.userData,
      };
    case NotificationActionTypes.SEND_UNREGISTER_USER:
      return {
        ...state,
        userData: initialUserData,
        unregistrationSuccessMessage: action.message,
      };
    case NotificationActionTypes.UPDATE_SUCCESS_MESSAGE:
      return {
        ...state,
        userData: initialUserData,
        updateSuccessMessage: action.message,
      };
    case NotificationActionTypes.CLEAR_UNREGISTRATION_SUCCESS_MESSAGE:
      return {
        ...state,
        unregistrationSuccessMessage: action.message,
      };
    case NotificationActionTypes.REQUEST_ACTIVE_NOTIFICATIONS:
      return {
        ...state,
        isFetchingNotificationNames: true,
      };
    case NotificationActionTypes.RECEIVED_ACTIVE_NOTIFICATIONS:
      return {
        ...state,
        isFetchingNotificationNames: false,
        activeNotifications: action.values,
      };
    case NotificationActionTypes.UPDATE_NOTIFICATION_ACTIVE:
      return {
        ...state,
        activeNotifications: state.activeNotifications.map(
          (notification, index) =>
            index === action.index
              ? {
                  ...notification,
                  NotificationActive: !notification.NotificationActive,
                }
              : notification
        ),
      };
    case NotificationActionTypes.RECEIVED_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE:
      const fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'NOTIFICATIONS_FETCH_ERROR',
      };
      return {
        ...state,
        fetchError: fetchErrorData,
      };
    case NotificationActionTypes.RESET_NOTIFICATIONS_HTTP_RESPONSE_ERROR_CODE:
      const resetError = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetError,
      };
    default:
      break;
  }

  return state;
};
